import React, { useContext, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Controller, useForm } from 'react-hook-form'
import * as api from '@/apis/aipass'
import { times } from '@/libs/times'
import { listContainerStyle } from '@/constants/layout'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { EditFooter } from '@/components/organisms/edit-footer'
import { InputField } from '@/components/molecules/input-field'
import { initializeSelectBoxStyle } from '@/constants/form'
import { CropModal } from '@/components/molecules/settings/crop-modal'
import { readImageFile, ALLOWED_MIME_TYPES } from '@/utils/image-file'
import { useSettingsEditPageGuardEffect } from '@/hooks/use-settings-edit-guard'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { AccountContext, AccountContextValue } from '@/contexts/account'
import { HotelBasicInfo, HotelBasicInfoInput } from '@/models/hotel/basic-info'

const toFormValue = (basicInfo: HotelBasicInfo): HotelBasicInfoInput => ({
  hotelName: basicInfo.hotelName,
  hotelNameLatin: basicInfo.hotelNameLatin,
  telephone: basicInfo.telephone,
  checkinBeginTime: basicInfo.checkinBeginTime,
  checkinEndTime: basicInfo.checkinEndTime,
  checkoutBeginTime: basicInfo.checkoutBeginTime,
  checkoutEndTime: basicInfo.checkoutEndTime,
  frontOpenTime: basicInfo.frontOpenTime,
  frontCloseTime: basicInfo.frontCloseTime,
  logoImageId: basicInfo.logoImageId,
  extImageId: basicInfo.extImageId,
})

export const SettingAccountEdit: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { errorHandler } = useErrorHandler()
  const { account, fetchAdminAccount } = useContext<AccountContextValue>(AccountContext)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [displayLogoAnnotation, setDisplayLogoAnnotation] = useState<boolean>(false)
  const [displayAnnotation, setDisplayAnnotation] = useState<boolean>(false)
  const [cropModalOpen, setCropModalOpen] = useState<boolean>(false)
  const [cropModalType, setCropModalType] = useState<'logo' | 'ext'>('logo')
  const [cropImageSrc, setCropImageSrc] = useState<string>('')

  const {
    control,
    register,
    reset,
    watch,
    setValue,
    handleSubmit,
    formState: { isValid },
  } = useForm<HotelBasicInfoInput>({
    mode: 'all',
    defaultValues: {
      hotelName: '',
      hotelNameLatin: '',
      telephone: '',
      email: '',
      checkinBeginTime: '',
      checkinEndTime: '',
      checkoutBeginTime: '',
      checkoutEndTime: '',
      frontOpenTime: '',
      frontCloseTime: '',
      logoImageId: null,
      extImageId: null,
    },
  })

  const logoImageId = watch('logoImageId')
  const extImageId = watch('extImageId')

  const handleChangeFile = async (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'ext') => {
    const file = e.target.files?.[0]
    if (!file) return
    const dataUrl = await readImageFile(file, t)
    if (!dataUrl) return
    setCropImageSrc(dataUrl)
    setCropModalType(type)
    setCropModalOpen(true)
  }

  const onCloseCropModal = () => {
    setCropImageSrc('')
    setCropModalOpen(false)
  }

  const onSaveData = async (formValue: HotelBasicInfoInput) => {
    try {
      setIsLoading(true)
      await api.updateFacilityBasicInfo(formValue)
      // 一覧はメールアドレスを account から表示するため、保存後に最新化する
      await fetchAdminAccount()
      history.push({ pathname: '/setting/account' })
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  useSettingsEditPageGuardEffect({
    redirectPath: '/setting/account',
    onAllowed: () => {
      setIsLoading(true)
      api
        .fetchFacilityBasicInfo()
        .then(res => {
          // fetchFacilityBasicInfo は API 層で例外を握り潰すため undefined を取得失敗とみなす。
          // 未取得のまま編集させると保存時に既存データを空で上書きしてしまう
          if (!res) {
            alert(t('Communication failed'))
            history.replace({ pathname: '/setting/account' })
            return
          }
          // メールアドレスは /admin-facility のレスポンスに含まれないため、アカウント情報を入力元にする
          reset({ ...toFormValue(res.basicInfo), email: account?.hotel?.email ?? '' })
        })
        .finally(() => setIsLoading(false))
    },
  })

  const footerContent = <EditFooter onCancel={history.goBack} onSave={handleSubmit(onSaveData)} isDisabledSave={!isValid} />

  return (
    <SettingsLayout loading={isLoading} footerContent={footerContent}>
      <div css={hotelAccountDetailsContainerStyle}>
        <div css={detailsHeaderContainerStyle}>
          <div css={detailsHeaderTitleStyle}>{t('Facility information')}</div>
        </div>
        <div css={detailsMainContainerStyle}>
          <ul css={detailsMainLeftContainerStyle}>
            <li css={hotelLogoList}>
              <div css={hotelLogoHelpDiv}>
                <p css={mainLeftTopLabelStyle}>{t('Hotel logo')}</p>
                <img
                  css={helpIconStyle}
                  src={require('@/static/images/icon_help.svg')}
                  onMouseEnter={() => setDisplayLogoAnnotation(true)}
                  onMouseLeave={() => setDisplayLogoAnnotation(false)}
                ></img>
              </div>
              <div css={hotelLogoImageWrapperStyle}>
                <label css={hotelLogoImageInputStyle} htmlFor="hotelLogoImage" />
                <input
                  type="file"
                  id="hotelLogoImage"
                  css={hotelLogoImageInputNoneStyle}
                  onChange={e => handleChangeFile(e, 'logo')}
                  onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                    e.currentTarget.value = ''
                  }}
                  accept={ALLOWED_MIME_TYPES.join(',')}
                />
                {logoImageId && (
                  <div css={hotelLogoImage} style={{ backgroundImage: `url(${logoImageId})` }}>
                    <img
                      src={require('@/static/images/deleat_icon.svg')}
                      css={deleteHotelLogoIconStyle}
                      alt="delete icon"
                      onClick={e => {
                        e.stopPropagation()
                        setValue('logoImageId', null, { shouldDirty: true })
                      }}
                    />
                  </div>
                )}
              </div>
              {displayLogoAnnotation && <div css={imageAnnotationModal}></div>}
            </li>
          </ul>
          {cropModalOpen && (
            <CropModal
              value={cropImageSrc}
              title={cropModalType === 'logo' ? t('Set hotel logo') : t('Set hotel image')}
              aspect={cropModalType === 'logo' ? 1 : 2 / 1}
              cropShape={cropModalType === 'logo' ? 'round' : 'rect'}
              onUpload={imageUrl => {
                setValue(cropModalType === 'logo' ? 'logoImageId' : 'extImageId', imageUrl, { shouldDirty: true })
              }}
              onClose={onCloseCropModal}
              feature="hotel"
              isPublic={true}
            />
          )}
          <ul css={detailMainRightContainerStyle}>
            <li css={hotelImageList}>
              <div css={hotelLogoHelpDiv}>
                <p css={mainLeftLabelStyle}>{t('Hotel image')}</p>
                <img
                  css={helpIconStyle}
                  src={require('@/static/images/icon_help.svg')}
                  onMouseEnter={() => setDisplayAnnotation(true)}
                  onMouseLeave={() => setDisplayAnnotation(false)}
                ></img>
              </div>
              <div>
                <label htmlFor="hotelPicture" css={hotelPictureStyle}></label>
                <input
                  type="file"
                  id="hotelPicture"
                  css={hotelPictureInputNoneStyle}
                  onChange={e => handleChangeFile(e, 'ext')}
                  onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                    e.currentTarget.value = ''
                  }}
                  accept={ALLOWED_MIME_TYPES.join(',')}
                />
                {extImageId && (
                  <div css={hotelImageStyle} style={{ backgroundImage: `url(${extImageId})` }}>
                    <img
                      src={require('@/static/images/deleat_icon.svg')}
                      css={deleteExtIconStyle}
                      alt="delete icon"
                      onClick={e => {
                        e.stopPropagation()
                        setValue('extImageId', null, { shouldDirty: true })
                      }}
                    />
                  </div>
                )}
              </div>
              {displayAnnotation && <div css={hotelIimageAnnotationModal}></div>}
            </li>
          </ul>
        </div>
        <div css={detailsMainContainerStyle}>
          <ul css={detailsMainLeftContainerStyle}>
            <li css={facilityNameList}>
              <div css={mainLeftTopLabelStyle}>{t('Name of facility')}</div>
              <div css={listDetailTitle}>{t('A name of a hotel')}</div>
              <Controller
                name="hotelName"
                control={control}
                rules={{ required: t('Required field has not been entered') }}
                render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                  <InputField
                    marginBottom={0}
                    width="90%"
                    placeholder={'HOTEL CUICIN'}
                    value={value ?? ''}
                    name={name}
                    handleChangeData={onChange}
                    error={error?.message}
                  />
                )}
              />
            </li>
            <li css={facilityNameList}>
              <div css={listDetailTitle}>{t('Hotel name (English display)')}</div>
              <Controller
                name="hotelNameLatin"
                control={control}
                rules={{ required: t('Required field has not been entered') }}
                render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                  <InputField
                    marginBottom={0}
                    width="90%"
                    placeholder={'HOTEL CUICIN'}
                    value={value ?? ''}
                    name={name}
                    handleChangeData={onChange}
                    error={error?.message}
                  />
                )}
              />
            </li>
          </ul>
          <ul css={detailMainRightContainerStyle}>
            <div css={checkinOutLists}>
              <div css={mainLeftTopLabelStyle}>{t('Check-in/out')}</div>
              <li>
                <div css={listDetailTitle}>{t('Check-in time')}</div>
                <div css={basicDetailContentsSelectContainerStyle}>
                  <select css={basicDetailContentsSelectBoxStyle} {...register('checkinBeginTime')}>
                    {times.map((item, index) => (
                      <option key={index} value={item['value']}>
                        {item['name']}
                      </option>
                    ))}
                  </select>
                  <div css={basicDetailContentsSepStyle}>〜</div>
                  <select css={basicDetailContentsSelectBoxStyle} {...register('checkinEndTime')}>
                    {times.map((item, index) => (
                      <option key={index} value={item['value']}>
                        {item['name']}
                      </option>
                    ))}
                  </select>
                </div>
              </li>
              <li>
                <div css={listDetailTitle}>{t('Check out time')}</div>
                <div css={basicDetailContentsSelectContainerStyle}>
                  <select css={basicDetailContentsSelectBoxStyle} {...register('checkoutBeginTime')}>
                    {times.map((item, index) => (
                      <option key={index} value={item['value']}>
                        {item['name']}
                      </option>
                    ))}
                  </select>
                  <div css={basicDetailContentsSepStyle}>〜</div>
                  <select css={basicDetailContentsSelectBoxStyle} {...register('checkoutEndTime')}>
                    {times.map((item, index) => (
                      <option key={index} value={item['value']}>
                        {item['name']}
                      </option>
                    ))}
                  </select>
                </div>
              </li>
            </div>
          </ul>
        </div>
        <div css={detailsMainContainerStyle}>
          <ul css={detailsMainLeftContainerStyle}>
            <div css={frontInfoLists}>
              <div css={mainLeftTopLabelStyle}>{t('Front desk information')}</div>
              <li>
                <p css={listDetailTitle}>{t('Phone number')}</p>
                <Controller
                  name="telephone"
                  control={control}
                  render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                    <InputField
                      marginBottom={0}
                      width="90%"
                      placeholder={'08012345678'}
                      value={value ?? ''}
                      name={name}
                      handleChangeData={onChange}
                      error={error?.message}
                    />
                  )}
                />
              </li>
              <li>
                <p css={listDetailTitle}>{t('Email address')}</p>
                <Controller
                  name="email"
                  control={control}
                  render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                    <InputField
                      marginBottom={0}
                      width="90%"
                      placeholder="aipass@aipass.inc"
                      value={value ?? ''}
                      name={name}
                      handleChangeData={onChange}
                      error={error?.message}
                    />
                  )}
                />
              </li>
              <li>
                <div css={listDetailTitle}>{t('Front desk hours')}</div>
                <div css={basicDetailContentsSelectContainerStyle}>
                  <select css={basicDetailContentsSelectBoxStyle} {...register('frontOpenTime')}>
                    {times.map((item, index) => (
                      <option key={index} value={item['value']}>
                        {item['name']}
                      </option>
                    ))}
                  </select>
                  <div css={basicDetailContentsSepStyle}>〜</div>
                  <select css={basicDetailContentsSelectBoxStyle} {...register('frontCloseTime')}>
                    {times.map((item, index) => (
                      <option key={index} value={item['value']}>
                        {item['name']}
                      </option>
                    ))}
                  </select>
                </div>
              </li>
            </div>
          </ul>
        </div>
      </div>
    </SettingsLayout>
  )
}

// topStyle
const hotelAccountDetailsContainerStyle = css(listContainerStyle, {
  width: '100%',
  background: '#FFF',
  minHeight: 410,
})

const detailsHeaderContainerStyle = css({
  height: 50,
  padding: '0 32px',
  borderBottom: '1px solid #F2F2F2',
})

const detailsHeaderTitleStyle = css({
  fontSize: 18,
  fontWeight: 'bold',
  padding: '16px 0',
})

const detailsMainContainerStyle = css({
  display: 'flex',
  borderBottom: '1px solid #F2F2F2',
})

const detailsMainLeftContainerStyle = css({
  width: '50%',
  borderRight: '1px solid #F2F2F2',
})

const mainLeftTopLabelStyle = css({
  fontSize: 16,
  fontWeight: 'bold',
  color: '#272727',
  marginRight: 10,
  marginBottom: 20,
})
const mainLeftLabelStyle = css(mainLeftTopLabelStyle)
const hotelLogoImageWrapperStyle = css({
  width: 120,
  margin: '0 auto',
})

const hotelLogoImageInputStyle = css({
  cursor: 'pointer',
  width: 120,
  height: 120,
  '&::after': {
    display: 'inline-block',
    width: 120,
    height: 120,
    borderRadius: '50%',
    backgroundSize: '20%',
    content: '""',
    verticalAlign: 'middle',
    backgroundImage: `url(${require('@/static/images/camera_icon_white.svg')})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
})
const hotelLogoImageInputNoneStyle = css({
  position: 'absolute',
  display: 'none',
})

const hotelPictureInputNoneStyle = css(hotelLogoImageInputNoneStyle)

const hotelPictureStyle = css({
  '&::after': {
    display: 'inline-block',
    width: '100%',
    height: 159,
    content: '""',
    verticalAlign: 'middle',
    backgroundImage: `url(${require('@/static/images/camera_icon_white.svg')})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
    backgroundSize: 30,
    borderRadius: 5,
  },
})

const detailMainRightContainerStyle = css({
  width: '50%',
})

const hotelLogoList = css({
  margin: 32,
})
const hotelImageList = css(hotelLogoList)
const facilityNameList = css(hotelLogoList)
const checkinOutLists = css(hotelLogoList)
const frontInfoLists = css(hotelLogoList)

const listDetailTitle = css({
  fontSize: '12px',
  fontWeight: 'bold',
  color: '#676767',
  margin: '10px 0 10px 0',
})
const basicDetailContentsSelectBoxStyle = css(initializeSelectBoxStyle, {
  width: 115,
  height: 32,
  border: '1px solid #CCCCCC',
  borderRadius: 16,
  padding: '0 30px 0 16px',
  backgroundImage: `url(${require('@/static/images/select.svg')})`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: '85px center',
  backgroundSize: 10,
  display: 'block',
  cursor: 'pointer',
})
const basicDetailContentsSelectContainerStyle = css({
  display: 'flex',
  alignItems: 'center',
})
const basicDetailContentsSepStyle = css({
  margin: '0 8px',
})

const modalStyle = css({
  '&::after': {
    boxShadow: '0 10px 25px 0 #90979e7a',
    padding: '15px 15px 15px 11px',
    fontSize: '12px',
    color: '#272727',
    borderRadius: 5,
    lineHeight: 1.5,
    zIndex: 103,
    display: 'inline-block',
    whiteSpace: 'pre-wrap',
    backgroundSize: 'contain',
    verticalAlign: 'middle',
    backgroundColor: '#fff',
  },
})
const imageAnnotationModal = css(modalStyle, {
  '&::after': {
    width: 260,
    margin: '-330px 0px -28px 121px',
    content: '"※使用できる画像形式は、jpg、pngのみです\\A※最大サイズ　240px×240px\\A※推奨サイズ　160px×160px"',
    whiteSpace: 'pre',
  },
})

const hotelIimageAnnotationModal = css(modalStyle, {
  '&::after': {
    width: 280,
    margin: '-450px 0px 0px 121px',
    content: '"※使用できる画像形式は、jpg、pngのみです。\\A※最大サイズ　2304px×984px \\A※推奨サイズ　1536px×656px"',
  },
})

const hotelLogoImage = css({
  width: 120,
  height: 120,
  borderRadius: '50%',
  backgroundPosition: 'center center',
  backgroundSize: '100%',
  backgroundRepeat: 'no-repeat',
  margin: '-120px 0px 0px 0px',
  position: 'relative',
})

const hotelImageStyle = css({
  width: '100%',
  height: 158,
  margin: '-158px 0 0 0',
  borderRadius: 5,
  backgroundSize: 'cover',
  position: 'relative',
})

const hotelLogoHelpDiv = css({
  display: 'flex',
  paddingBottom: 10,
  alignItems: 'flex-start',
})

const helpIconStyle = css({
  cursor: 'pointer',
})

const deleteExtIconStyle = css({
  position: 'absolute',
  right: -10,
  top: -10,
  width: 20,
  height: 20,
  pointerEvents: 'auto',
  cursor: 'pointer',
})

const deleteHotelLogoIconStyle = css({
  position: 'absolute',
  right: -6,
  top: -6,
  width: 20,
  height: 20,
  pointerEvents: 'auto',
  cursor: 'pointer',
})
